import { useOutletContext } from 'react-router-dom'

import { LegalEntityDetailsDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import PrimoboxStatusRow from 'modules/integrations_primobox/components/StatusRow'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import AddressBlock from 'modules/settings/components/AddressBlock'
import CurrencyBlock from 'modules/settings/components/CurrencyBlock'
import IbanBlock from 'modules/settings/components/IbanBlock'
import IdentificationBlock from 'modules/settings/components/IdentificationBlock'
import RepresentativeBlock from 'modules/settings/components/RepresentativeBlock'
import WorkplaceBlock from 'modules/settings/components/WorkplaceBlock'

const Details = () => {
  const { legalEntity, loading, error } = useOutletContext<{
    legalEntity: LegalEntity
    loading: boolean
    error: boolean
  }>()

  const result = useQuery(LegalEntityDetailsDocument, {
    variables: { companyId: legalEntity.attributes.company_id, legalEntityId: legalEntity.id },
  })

  const legalEntityStatus =
    result.data?.integrationsPrimobox?.legalEntityStatusesConnection?.nodes[0]

  const features = result.data?.features?.featuresConnection.nodes || []

  const hasFeature = (featureName: string) =>
    features.find((feature) => feature.name === featureName)?.enabled

  const hasPayroll = hasFeature('payroll')
  const hasNewPayroll = hasFeature('payroll_bundle')
  const hasPrimobox = hasFeature('integrations_primobox_active')
  const hasWorkplaceSelector = hasFeature('dev_job_catalog_mle_migration_preparation')

  const workplaceData = result.data?.locations?.legalEntityWorkplaces?.[0] ?? null
  const canSetWorkplace = result.data?.locations?._policies?.legalEntityWorkplaceSet?.can ?? false

  return (
    <ResponsivePage.Body loading={loading} error={error} showHeader={false}>
      <IdentificationBlock legalEntity={legalEntity} />
      <CurrencyBlock legalEntity={legalEntity} />
      <AddressBlock legalEntity={legalEntity} />
      {hasWorkplaceSelector && canSetWorkplace && (
        <WorkplaceBlock legalEntity={legalEntity} workplaceData={workplaceData} />
      )}
      {hasPrimobox && legalEntityStatus && (
        <PrimoboxStatusRow
          cardTitle={i18n.t('integrations_primobox.status_card.legal_entity.title')}
          status={legalEntityStatus}
        />
      )}
      {hasPayroll && !hasNewPayroll && (
        <>
          <RepresentativeBlock legalEntity={legalEntity} />
          <IbanBlock legalEntity={legalEntity} />
        </>
      )}
    </ResponsivePage.Body>
  )
}

export default Details
